import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  PaymentTransactionFilterMeta,
  PaymentTransactionFilters,
  PaymentTransactionRow,
  PaymentTransactionsQueryParams,
  PaymentTransactionsResult,
} from "../../types/paymentTransactions";
import { getPrivateInstance } from "../_privateInstance";

export const PAYMENT_TRANSACTIONS_QUERY_KEY =
  "PAYMENT_TRANSACTIONS" as const;

const FILTER_PARAM_KEYS: Record<
  keyof PaymentTransactionFilters,
  string
> = {
  transaction_type: "transaction_type[eq]",
  platform: "platform[eq]",
  geographic_subdivision: "geographic_subdivision[eq]",
  user_id: "user_id[eq]",
  payment_status: "payment_status[eq]",
  money_type: "money_type[eq]",
  amount_min: "amount[ge]",
  amount_max: "amount[le]",
};

const isPresent = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "";

export function buildPaymentTransactionFilterParams(
  filters: PaymentTransactionFilters,
): Record<string, string> {
  const params: Record<string, string> = {};

  (Object.keys(FILTER_PARAM_KEYS) as Array<
    keyof PaymentTransactionFilters
  >).forEach((key) => {
    const value = filters[key];
    if (isPresent(value)) {
      params[FILTER_PARAM_KEYS[key]] = value.trim();
    }
  });

  return params;
}

export function buildPaymentTransactionsParams(
  args: PaymentTransactionsQueryParams,
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    start_date: args.start_date,
    end_date: args.end_date,
    page: args.page,
    size: args.size,
    sort: args.sort,
  };

  if (isPresent(args.search)) {
    params.search = args.search.trim();
  }

  return { ...params, ...buildPaymentTransactionFilterParams(args) };
}

const finiteNumber = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

export function parsePaymentTransactionsResponse(
  body: unknown,
): PaymentTransactionsResult {
  const response = body as {
    data?: {
      items?: PaymentTransactionRow[];
      total?: number;
      page?: number;
      size?: number;
      pages?: number;
      meta?: { filters?: PaymentTransactionFilterMeta[] };
    };
    meta?: { filters?: PaymentTransactionFilterMeta[] };
  } | null;
  const items = Array.isArray(response?.data?.items)
    ? response.data.items
    : [];
  const total = finiteNumber(response?.data?.total, items.length);
  const page = finiteNumber(response?.data?.page, 1);
  const size = finiteNumber(response?.data?.size, items.length);
  const pages = finiteNumber(
    response?.data?.pages,
    size > 0 ? Math.ceil(total / size) : 0,
  );
  const filterSource =
    response?.meta?.filters ?? response?.data?.meta?.filters;
  const filters = Array.isArray(filterSource) ? filterSource : [];

  return { items, total, page, size, pages, filters };
}

export async function fetchPaymentTransactions(
  args: PaymentTransactionsQueryParams,
): Promise<PaymentTransactionsResult> {
  const response = await getPrivateInstance().get(
    API_ROUTES.PAYMENT_TRANSACTIONS,
    { params: buildPaymentTransactionsParams(args) },
  );
  return parsePaymentTransactionsResponse(response?.data);
}

export function usePaymentTransactions(
  args: PaymentTransactionsQueryParams,
) {
  const paramsToken = JSON.stringify(buildPaymentTransactionsParams(args));
  const query = useQuery({
    queryKey: [PAYMENT_TRANSACTIONS_QUERY_KEY, paramsToken],
    queryFn: () => fetchPaymentTransactions(args),
    enabled: Boolean(args.start_date && args.end_date),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    items: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    page: query.data?.page ?? args.page,
    size: query.data?.size ?? args.size,
    pages: query.data?.pages ?? 0,
    filters: query.data?.filters ?? [],
  };
}

export default usePaymentTransactions;
